/**
 * Why a worker is deliberately NOT paid from jobs.
 *
 * Mirrors the `job_pay_exemption` Postgres enum added in fieldiq-api migration
 * 0339, and declared there as `jobPayExemptionEnum` in `src/db/schema.ts`. Same
 * relationship `shop-pay.ts` has to `SHOP_RATE_GAP_MESSAGES`: a copy with no
 * database behind it, so it can drift silently, and the test beside it is what
 * stops that.
 *
 * ── NULL IS A DECISION, NOT AN ABSENCE ──────────────────────────────────────
 * `null` means the worker IS paid from jobs, and that is the overwhelmingly
 * common case. It is the one value in this file that must NEVER render as an em
 * dash: "—" would say the setting is missing, when what it actually says is
 * "this person is paid the ordinary way". CLAUDE.md's absent-renders-as-dash
 * rule is about values nobody supplied; this one is supplied by the column's
 * default and means something.
 *
 * The distinction the API is protecting (see the comment on `safeUser` in
 * users.routes.ts) is between "nobody has decided" and "someone decided they are
 * paid normally". On this column those are the same state by design, and the
 * label below says the second one out loud rather than leaving a gap a reader
 * fills in for themselves.
 *
 * ── WHY THIS EXISTS AT ALL ──────────────────────────────────────────────────
 * The absence of an `employee_pay_rates` row used to mean two things at once:
 * "no rate configured yet", which the office should act on, and "not paid from
 * jobs", which nobody should. Every per-worker gap report counted the second as
 * the first, permanently — a number that never went down however many rates were
 * entered, so it stopped being read.
 */

/** Every value of the API's `job_pay_exemption` enum. Order is the API's. */
export const JOB_PAY_EXEMPTIONS = ['dividends', 'salaried', 'sub_billed'] as const;

export type JobPayExemption = (typeof JOB_PAY_EXEMPTIONS)[number];

/**
 * What a worker's pay arrangement is, including the ordinary one.
 *
 * `null` is job-paid. Kept in the same type as the exemptions rather than
 * modelled separately, because every consumer has to answer "how is this person
 * paid" and only some of them care that the answer happens to be the default.
 */
export type JobPayArrangement = JobPayExemption | null;

/**
 * The label for "paid from jobs".
 *
 * Named rather than written at each call site: it is the DEFAULT option in the
 * setter, the render for a null value, and the wording the brief specified, so
 * three surfaces would otherwise each hold their own copy of it.
 */
export const JOB_PAID_LABEL = 'Paid from jobs';

/**
 * Plain-language labels, one entry per enum value.
 *
 * `Record<JobPayExemption, …>` rather than `Record<string, …>` is the point:
 * adding a fourth value to the union fails the typecheck here instead of
 * silently rendering as a raw enum string. Per CLAUDE.md, labels come from a
 * shared exhaustive map, never a ternary that falls through to the raw value.
 *
 * These are what a dispatcher reads, so none of them is the enum value dressed
 * up — "sub_billed" is not "Sub billed".
 */
export const JOB_PAY_EXEMPTION_LABELS: Record<JobPayExemption, string> = {
  dividends: 'Paid by dividends',
  salaried: 'On salary',
  sub_billed: 'Bills as a subcontractor',
};

/**
 * The sentence under each label in the setter — what the arrangement means, in
 * the words someone choosing it would use. Kept beside the labels so the two
 * cannot disagree about what an option does.
 */
export const JOB_PAY_EXEMPTION_DESCRIPTIONS: Record<JobPayExemption, string> = {
  dividends: 'Takes dividends from the company rather than pay for job work.',
  salaried: 'On a salary that already covers job work, so jobs do not add to it.',
  sub_billed: 'Invoices the company as a subcontractor rather than going through payroll.',
};

/** Narrow an unknown to a value this build recognises. */
export function isJobPayExemption(value: unknown): value is JobPayExemption {
  return typeof value === 'string' && (JOB_PAY_EXEMPTIONS as readonly string[]).includes(value);
}

/**
 * Whether a worker is exempt at all.
 *
 * TRUE for a value this build does not recognise. A newer API teaching this
 * screen a fourth exemption must not read as "paid from jobs" — that is the one
 * wrong answer here, because it would send the office to configure a rate for
 * somebody a tenant admin has deliberately taken off job pay. Unknown-but-set is
 * still set.
 *
 * `''` counts as NOT exempt: the API folds an empty string to null on write
 * (a blank option in a select posts one), so it means job-paid there too.
 */
export function isJobPayExempt(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * The label for any arrangement, recognised or not.
 *
 * Never echoes a raw enum value, and never guesses. An unrecognised value gets a
 * sentence that is true of every possible one — the worker is not on job pay —
 * rather than a made-up specific. A fallback that echoes the raw value is how a
 * display bug gets reported as a data bug.
 */
export function jobPayArrangementLabel(value: unknown): string {
  if (!isJobPayExempt(value)) return JOB_PAID_LABEL;
  if (isJobPayExemption(value)) return JOB_PAY_EXEMPTION_LABELS[value];
  return 'Not paid from jobs';
}
